import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button, Label } from 'lift-ui';
import { Plus, Trash2, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { OVERVIEW_MAX_ITEMS, OVERVIEW_MAX_ITEM_LEN } from '../api';

interface StringListEditorProps {
  label: string;
  helperText?: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  /** Index of the row the user is hovering/editing, so the preview can follow. */
  onActiveIndexChange?: (index: number | null) => void;
}

const AutoGrowTextarea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }
> = ({ value, className = '', ...props }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      className={`w-full resize-none rounded-md border bg-white px-3 py-2 text-sm leading-snug text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 ${className}`}
      {...props}
    />
  );
};

/**
 * Ordered list editor for the Overview config string lists (Popular Tasks,
 * Sample Prompts, Pairs With). Order is meaningful — it is the display order —
 * so rows can be dragged or nudged up/down. Empty rows are allowed while
 * editing and are trimmed out by the parent before saving.
 */
const StringListEditor: React.FC<StringListEditorProps> = ({
  label,
  helperText,
  items,
  onChange,
  placeholder,
  onActiveIndexChange,
}) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const atMax = items.length >= OVERVIEW_MAX_ITEMS;

  const updateAt = (index: number, value: string) => {
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  const removeAt = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
    onActiveIndexChange?.(null);
  };

  const addItem = () => {
    if (atMax) return;
    onChange([...items, '']);
  };

  const moveTo = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
    onActiveIndexChange?.(to);
  };

  const handleDrop = (index: number) => {
    if (dragIndex !== null) moveTo(dragIndex, index);
    setDragIndex(null);
    setDropIndex(null);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <Label className="text-sm font-semibold text-gray-800">{label}</Label>
        <span className={`text-xs ${atMax ? 'text-amber-600' : 'text-gray-400'}`}>
          {items.length}/{OVERVIEW_MAX_ITEMS}
        </span>
      </div>
      {helperText && <p className="-mt-1 text-xs text-gray-500">{helperText}</p>}

      <div className="flex flex-col gap-1.5">
        {items.length === 0 && (
          <p className="py-1 text-xs italic text-gray-400">No items yet.</p>
        )}
        {items.map((item, index) => {
          const tooLong = item.length > OVERVIEW_MAX_ITEM_LEN;
          return (
            <div
              key={index}
              onDragOver={(e) => {
                if (dragIndex === null) return;
                e.preventDefault();
                setDropIndex(index);
              }}
              onDrop={() => handleDrop(index)}
              onMouseEnter={() => onActiveIndexChange?.(index)}
              onMouseLeave={() => onActiveIndexChange?.(null)}
              className={`group flex items-start gap-1.5 rounded-md p-1 transition-colors ${
                dropIndex === index && dragIndex !== index
                  ? 'bg-indigo-50'
                  : 'hover:bg-gray-50'
              } ${dragIndex === index ? 'opacity-50' : ''}`}
            >
              <div
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragEnd={() => {
                  setDragIndex(null);
                  setDropIndex(null);
                }}
                title="Drag to reorder"
                className="mt-2 flex cursor-grab items-center text-gray-300 active:cursor-grabbing group-hover:text-gray-400"
              >
                <GripVertical className="h-4 w-4" />
              </div>
              <span className="mt-2 w-4 shrink-0 text-right text-xs tabular-nums text-gray-400">
                {index + 1}
              </span>

              <div className="min-w-0 flex-1">
                <AutoGrowTextarea
                  value={item}
                  placeholder={placeholder}
                  onChange={(e) => updateAt(index, e.target.value)}
                  onFocus={() => onActiveIndexChange?.(index)}
                  onBlur={() => onActiveIndexChange?.(null)}
                  className={
                    tooLong
                      ? 'border-red-400 focus:ring-red-200'
                      : 'border-gray-200'
                  }
                />
                {tooLong && (
                  <p className="mt-0.5 text-xs text-red-500">
                    Max {OVERVIEW_MAX_ITEM_LEN} characters ({item.length}).
                  </p>
                )}
              </div>

              <div className="mt-1 flex shrink-0 items-center">
                <button
                  type="button"
                  onClick={() => moveTo(index, index - 1)}
                  disabled={index === 0}
                  className="rounded p-1 text-gray-300 hover:bg-gray-100 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-30"
                  aria-label="Move up"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveTo(index, index + 1)}
                  disabled={index === items.length - 1}
                  className="rounded p-1 text-gray-300 hover:bg-gray-100 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-30"
                  aria-label="Move down"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeAt(index)}
                  className="rounded p-1 text-gray-300 hover:bg-red-50 hover:text-red-600"
                  aria-label="Remove item"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addItem}
          disabled={atMax}
          className="flex h-8 items-center gap-2 px-3"
        >
          <Plus className="h-4 w-4" />
          Add item
        </Button>
        {atMax && (
          <span className="ml-2 text-xs text-gray-400">
            Maximum {OVERVIEW_MAX_ITEMS} items reached.
          </span>
        )}
      </div>
    </div>
  );
};

export default StringListEditor;
